/**
 * Subscription / HRMS billing theme — aliases app brand (Patient indigo SoT).
 *
 * Covers Manage Billing (bill list) and Bill Detail (invoice sheet). Colors
 * still flow from patientTheme, so this module stays swappable.
 *
 * Note: `pages/Billing/billingTheme.js` is a different module (patient claims)
 * and keeps its own blue on purpose — do not merge the two.
 */
import { BRAND_THEME, BRAND_THEME_HEX } from "../ui/brandTheme";

export { BRAND_THEME as SUBSCRIPTION_THEME, BRAND_THEME_HEX as SUBSCRIPTION_THEME_HEX };

export const SUBSCRIPTION_BTN = {
  primary: `inline-flex items-center justify-center gap-2 h-9 px-4 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.primary}`,
  outline: `inline-flex items-center justify-center gap-2 h-9 px-4 text-sm font-medium rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.outline}`,
  /** In-row action — same size as the Patients list View button. */
  row: `inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded transition-colors disabled:opacity-50 ${BRAND_THEME.button.soft}`,
  danger:
    "inline-flex items-center justify-center gap-2 h-9 px-4 text-sm font-semibold rounded-md border border-red-600 bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50",
};

/** Page + card chrome shared by the bill list and the invoice sheet. */
export const SUBSCRIPTION_PAGE = {
  wrap: "p-6 sm:p-8 w-full",
  title: "text-2xl font-bold text-slate-900 tracking-tight",
  subtitle: "text-sm text-slate-500 mt-1",
  card: `bg-white rounded-xl border shadow-sm overflow-hidden ${BRAND_THEME.section.wrap}`,
  cardHead: `px-6 sm:px-8 py-4 border-b ${BRAND_THEME.section.head}`,
  cardTitle: `text-sm font-semibold uppercase tracking-wide ${BRAND_THEME.section.title}`,
  /** Muted band inside the invoice sheet (billing period, footer). */
  band: "bg-indigo-50/40 border-indigo-100",
  divider: "border-indigo-100",
  spinner:
    "animate-spin rounded-full h-10 w-10 border-2 border-indigo-600 border-t-transparent",
};

/**
 * Invoice line-item table (plain markup — the bill list uses the shared Table
 * with `headTone` instead).
 */
export const SUBSCRIPTION_TABLE = {
  headRow: `border-b ${BRAND_THEME.tableHead.row}`,
  headCell: `px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider ${BRAND_THEME.tableHead.cell}`,
  headCellRight: `px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider ${BRAND_THEME.tableHead.cell}`,
  body: "divide-y divide-indigo-50",
};

/** Bill list column widths for the shared Table (Sr. No. off). */
export const SUBSCRIPTION_COL = {
  billNumber: "whitespace-nowrap pl-4 pr-3",
  period: "whitespace-nowrap px-3",
  amount: "whitespace-nowrap px-3",
  dueDate: "whitespace-nowrap px-3",
  status: "whitespace-nowrap px-3",
  actions: "whitespace-nowrap pl-3 pr-4 text-right",
};

/**
 * Bill status pills stay semantic (paid / due / overdue) rather than brand —
 * same rule as the patient StatusBadge. Only PARTIAL uses the brand tone.
 */
export const SUBSCRIPTION_STATUS = {
  PAID: { className: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Paid" },
  UNPAID: { className: "bg-amber-50 text-amber-700 border-amber-200", label: "Unpaid" },
  OVERDUE: { className: "bg-red-50 text-red-700 border-red-200", label: "Overdue" },
  PARTIAL: { className: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "Partial" },
  CANCELLED: { className: "bg-slate-100 text-slate-600 border-slate-200", label: "Cancelled" },
  REFUNDED: { className: "bg-violet-50 text-violet-700 border-violet-200", label: "Refunded" },
};

export const SUBSCRIPTION_PILL =
  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-semibold";

export function subscriptionStatus(status) {
  return SUBSCRIPTION_STATUS[status] || SUBSCRIPTION_STATUS.UNPAID;
}
